<template>
  <div style="margin-top: 20px">
    <FiPageRail title="Create SOS Event" nowrapActions>
      <template #actions>
        <b-button size="sm" variant="outline-secondary" @click="showInfo = true" title="About this page" aria-label="About this page">
          <i class="fa fa-info-circle"></i>
        </b-button>
      </template>
    </FiPageRail>

    <b-modal v-model="showInfo" title="About Create SOS Event" ok-only ok-title="Close">
      <p>Raise a Bus SOS manually on behalf of a driver: drop a pin where help is needed, add the vehicle and driver details, then choose a mode.</p>
      <ul>
        <li><strong>Create Internal SOS</strong> — notify your company's dispatch recipients only.</li>
        <li><strong>Network Broadcast</strong> — also alert nearby opted-in companies (when your account is set to share with them in Bus SOS settings).</li>
      </ul>
    </b-modal>

    <div class="sos-send-wrap">
      <div class="sos-send-card" v-if="!submitted">
        <div class="sos-send-leadrow">
          <p v-if="yardOnly" class="sos-send-lead sos-send-lead--inline">Pick a location &amp; fill in what you can</p>
          <p v-else class="sos-send-lead sos-send-lead--inline">Enter a vehicle or pick a location</p>
        </div>
        <p class="sos-send-note">Your company's SOS recipients are always notified.</p>
        <!-- yard-only providers have no telematics: the pin IS the SOS location (a Vehicle ID is just a label) -->
        <p v-if="yardOnly" class="sos-send-note sos-send-note--yard-only"><i class="fa fa-map-marker"></i>&nbsp;Vehicle positions aren't tracked for your company &mdash; the pin is the SOS location.</p>

        <!-- ordered to match the driver page: Vehicle ID first, then address / pin drop, then the map -->
        <div class="sos-send-fields">
          <div class="sos-send-field-row">
            <div class="sos-send-field">
              <label class="sos-send-label">Vehicle ID</label>
              <b-input-group>
                <b-form-input v-model="vehicle_name" maxlength="120" autocomplete="off"
                              placeholder="e.g. BUS-204" @keyup.enter="locate_vehicle"/>
                <b-input-group-append>
                  <b-button variant="outline-secondary" :disabled="!vehicle_name || locatingVehicle"
                            @click="locate_vehicle"
                            title="Find this vehicle and set the pin to its current location"
                            aria-label="Find this vehicle and set the pin to its current location">
                    <b-spinner v-if="locatingVehicle" small></b-spinner>
                    <i v-else class="fa fa-search"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
          </div>
        </div>

        <!-- raise it by address OR by dropping a pin: either one fills the other -->
        <div class="sos-send-addr">
          <b-form-input v-model="address" placeholder="Enter an address…" autocomplete="off"
                        @keyup.enter="geocode_address" @blur="geocode_address"/>
          <button type="button" class="sos-icon-btn" @click="geocode_address" :disabled="geocoding || !address.trim()"
                  title="Find this address" aria-label="Find this address">
            <b-spinner v-if="geocoding" small></b-spinner>
            <i v-else class="fa fa-search"></i>
          </button>
          <button type="button" class="sos-icon-btn" @click="locate_me" :disabled="locating"
                  title="Use my location" aria-label="Use my location">
            <b-spinner v-if="locating" small></b-spinner>
            <i v-else class="fa fa-location-arrow"></i>
          </button>
        </div>

        <gmap-map
            ref="sendMap"
            :zoom="map_zoom"
            :center="map_center"
            :options="themed_map_options"
            class="sos-send-map"
            @click="place_pin">
          <gmap-marker v-if="pin_position" :position="pin_position" :icon="pinIcon" :draggable="true" @dragend="place_pin"/>
        </gmap-map>

        <div class="sos-send-pinline" v-if="pin_position">
          <i class="fa fa-map-marker"></i>&nbsp;{{ address || 'Location set on the map' }}
          <button type="button" class="sos-pin-clear" @click="clear_pin" title="Remove pin" aria-label="Remove pin"><i class="fa fa-times"></i>&nbsp;Remove pin</button>
        </div>
        <div class="sos-send-pinline sos-send-pinline--empty" v-else>
          Enter an address above, or tap the map to drop a pin.
        </div>

        <div class="sos-send-fields">
          <div class="sos-send-field-row">
            <div class="sos-send-field">
              <label class="sos-send-label">Driver name</label>
              <b-form-input v-model="driver_name" maxlength="120" autocomplete="off" @input="driverEdited"/>
            </div>
            <div class="sos-send-field">
              <label class="sos-send-label">Driver phone number</label>
              <b-form-input v-model="driver_phone" type="tel" maxlength="40" autocomplete="off" @input="driverEdited"/>
            </div>
          </div>

          <div class="sos-send-field-row">
            <div class="sos-send-field">
              <label class="sos-send-label">Operator Contact Name</label>
              <b-form-input v-model="operator_contact_name" maxlength="120" autocomplete="off"/>
            </div>
            <div class="sos-send-field sos-send-field--sm">
              <label class="sos-send-label">Operator Contact #</label>
              <b-form-input v-model="operator_contact_phone" type="tel" maxlength="40" autocomplete="off"/>
            </div>
            <div class="sos-send-field sos-send-field--wide">
              <label class="sos-send-label">Operator Contact Email</label>
              <b-form-input v-model="operator_contact_email" type="email" maxlength="200" autocomplete="off"/>
            </div>
          </div>

          <label class="sos-send-label">What's going on?</label>
          <b-form-textarea v-model="request_notes" maxlength="500" rows="3"
                           placeholder="Breakdown, accident, passengers on board, etc."/>
        </div>

        <div v-if="submit_error" class="sos-send-state sos-send-state--error">{{ submit_error }}</div>

        <div class="sos-send-actions">
          <b-button size="lg" variant="danger" class="sos-send-submit"
                    :disabled="!pin_position || submitting" @click="submit()">
            <span v-if="submitting"><b-spinner small></b-spinner>&nbsp;Creating&hellip;</span>
            <span v-else><i class="fa fa-bullhorn"></i>&nbsp;Create SOS</span>
          </b-button>
        </div>
        <div class="sos-send-modes">
          <span>Notifies your company's dispatch recipients.</span>
        </div>
      </div>

      <div class="sos-send-card sos-send-success" v-else-if="submitted && !notified">
        <div class="sos-send-warn-icon">&#9888;</div>
        <h2>SOS recorded &mdash; but not delivered</h2>
        <p>No SOS email or SMS recipients are configured, so <strong>no alert was sent to anyone</strong>. Add recipients in Bus SOS settings, and notify your dispatcher directly in the meantime.</p>
        <p v-if="request_id" class="sos-send-ref">Reference: {{ request_id }}</p>
        <div class="sos-send-successlinks">
          <b-button v-if="request_id" variant="outline-secondary" :to="{ path: 'sos_detail?request_id=' + request_id }">View SOS Details</b-button>
          <b-button variant="outline-secondary" :to="{ name: 'sos_list' }">SOS Events</b-button>
          <b-button variant="danger" @click="reset_form">Send another</b-button>
        </div>
      </div>

      <div class="sos-send-card sos-send-success" v-else-if="submitted">
        <div class="sos-send-check">&#10003;</div>
        <h2>SOS created</h2>
        <p>Sent to your company's dispatch recipients.</p>
        <p v-if="request_id" class="sos-send-ref">Reference: {{ request_id }}</p>
        <div class="sos-send-successlinks">
          <b-button v-if="request_id" variant="outline-secondary" :to="{ path: 'sos_detail?request_id=' + request_id }">View SOS Details</b-button>
          <b-button variant="outline-secondary" :to="{ name: 'sos_list' }">SOS Events</b-button>
          <b-button variant="danger" @click="reset_form">Send another</b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { send_sos_pin_request, get_sos_settings, geocode_sos_address, revgeocode_sos_pin, locate_sos_vehicle_authed } from '../networking/apis'
import { mapState } from 'vuex'
import { DARK_MAP_STYLES } from '../assets/dark_map_styles'

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }

export default {
  name: 'sos_send',
  data() {
    return {
      submitted: false,
      submitting: false,
      showInfo: false,
      submit_error: null,
      request_id: null,
      notified: true,
      locating: false,
      address: '',
      geocoding: false,
      pin_position: null,
      map_center: DEFAULT_CENTER,
      map_zoom: 4,
      yardOnly: false,   // yard-only provider (no telematics): the pin is the only location we have
      vehicle_name: '',
      locatingVehicle: false,
      driver_name: '',
      driver_phone: '',
      // true while the driver name/# were auto-filled from a vehicle lookup (so re-locating a
      // different vehicle may refresh or clear them); off the moment staff edit either field
      driverFromLookup: false,
      operator_contact_name: '',
      operator_contact_phone: '',
      operator_contact_email: '',
      // the tenant's SOS dispatch contact (from get_sos_settings), used to pre-fill the operator #/email
      sosContactPhone: '',
      sosContactEmail: '',
      request_notes: '',
      // matches the fleet map basemap (vehicle_map.vue); midnight users get the dark variant
      mapStyle: {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'landscape', stylers: [{ color: '#F3F4F9' }] },
          { featureType: 'water', stylers: [{ color: '#C1CDE4' }] },
          { featureType: 'landscape.natural.terrain', stylers: [{ color: '#E0EDCA' }] },
          { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#DDDDDD' }] },
          { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#FFFFFF' }] },
          { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#FEF8D6' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        ],
      },
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    themed_map_options() {
      // follow the SAME signal the page's CSS tokens use — the data-fi-theme attribute on <html> —
      // so the map's light/dark can never diverge from the panels. (Reading site_theme too keeps
      // this computed reactive to an in-session theme toggle.) Only the dark "midnight" horizon
      // variant gets dark tiles; the light horizon ('new') and 'original' keep the light basemap.
      const storeTheme = (this.$store.state.profile || {}).site_theme
      const attrTheme = (typeof document !== 'undefined' && document.documentElement.getAttribute('data-fi-theme')) || storeTheme
      const midnight = attrTheme === 'midnight'
      return midnight ? { ...this.mapStyle, styles: DARK_MAP_STYLES } : this.mapStyle
    },
    pinIcon() {
      // red dot (matches the SOS map range pin), not the default teardrop marker
      return {
        path: 'M 0,-7 A 7,7 0 1,1 0,7 A 7,7 0 1,1 0,-7 z',
        fillColor: '#c0392b',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.1,
      }
    },
  },
  mounted() {
    this.default_operator_contact()
    this.load_settings()
  },
  methods: {
    default_operator_contact() {
      const u = this.user || {}
      this.operator_contact_name = u.full_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || ''
      // default the operator contact #/email from the tenant's SOS dispatch contact, falling back
      // to the signed-in user's own email when no SOS contact is configured
      this.operator_contact_phone = this.sosContactPhone || ''
      this.operator_contact_email = this.sosContactEmail || u.email || ''
    },
    async load_settings() {
      try {
        const { value: { data = {} } } = await get_sos_settings(this.user.tenant)
        this.yardOnly = !!data.yard_only
        this.sosContactPhone = data.sos_contact_phone || ''
        this.sosContactEmail = data.sos_contact_email || ''
        // re-apply the operator-contact defaults now that the SOS contact has loaded (mounted()
        // ran default_operator_contact before this resolved)
        this.default_operator_contact()
      } catch (e) {
        this.yardOnly = false
      }
    },
    locate_me() {
      if (!navigator.geolocation) return
      this.locating = true
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.locating = false
          const p = { lat: pos.coords.latitude, lng: pos.coords.longitude }
          this.map_center = p
          this.map_zoom = 14
        },
        () => { this.locating = false },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      )
    },
    place_pin(ev) {
      this.pin_position = { lat: ev.latLng.lat(), lng: ev.latLng.lng() }
      this.reverse_geocode()   // drop a pin -> fill the address
    },
    clear_pin() {
      // remove a dropped pin and the location it set
      this.pin_position = null
      this.address = ''
    },
    applyDriver(driver) {
      // keep the driver name/# in step with the vehicle we just resolved: refresh to the newly-
      // resolved driver, or clear when none is found. Never touches a value staff typed by hand --
      // only a field WE auto-filled (driverFromLookup) is updated or cleared here.
      if (!this.driverFromLookup && (this.driver_name || this.driver_phone)) return
      const name = (driver && driver.name) || ''
      const phone = (driver && driver.phone) || ''
      this.driver_name = name
      this.driver_phone = phone
      this.driverFromLookup = !!(name || phone)
    },
    driverEdited() {
      // staff took over the driver fields; stop auto-managing them
      this.driverFromLookup = false
    },
    async locate_vehicle() {
      // enter a Vehicle ID -> resolve its live telematics position and set/update the pin there
      const v = (this.vehicle_name || '').trim()
      if (!v || this.locatingVehicle) return
      this.locatingVehicle = true
      try {
        const { value: { data = {} } } = await locate_sos_vehicle_authed(this.user.tenant, v)
        if (data.found && data.lat != null && data.lon != null) {
          this.pin_position = { lat: Number(data.lat), lng: Number(data.lon) }
          this.map_center = this.pin_position
          this.map_zoom = 15
          this.address = data.address || ''
          if (!this.address) this.reverse_geocode()
          // sync the driver currently paired to this bus (refresh or clear), leaving a typed value alone
          this.applyDriver(data.driver)
          this.$emit('showAlert', 'Found ' + (data.vehicle_name || v) + ' — pin set to its current location', 'success', 3000)
        } else if (data.yard_only) {
          this.applyDriver(null)
          this.$emit('showAlert', 'This company has no vehicle tracking — drop a pin on the map instead', 'warning', 5000)
        } else {
          // the vehicle changed to one we can't find -> drop the driver we auto-filled for the old one
          this.applyDriver(null)
          this.$emit('showAlert', "Couldn't locate '" + v + "' — check the ID or drop a pin on the map", 'warning', 5000)
        }
      } catch (e) {
        this.$emit('showAlert', "Couldn't look up that vehicle", 'danger', 4000)
      }
      this.locatingVehicle = false
    },
    async geocode_address() {
      // enter an address -> drop a pin on it
      const q = (this.address || '').trim()
      if (!q || this.geocoding) return
      this.geocoding = true
      try {
        const { value: { data = {} } } = await geocode_sos_address(this.user.tenant, q)
        if (data.found) {
          this.pin_position = { lat: Number(data.lat), lng: Number(data.lon) }
          this.map_center = this.pin_position
          this.map_zoom = 15
          if (data.address) this.address = data.address
        } else {
          this.$emit('showAlert', "couldn't find that address — check it or tap the map instead", 'warning', 4000)
        }
      } catch (e) {
        this.$emit('showAlert', "couldn't look up that address", 'danger', 4000)
      }
      this.geocoding = false
    },
    async reverse_geocode() {
      if (!this.pin_position) return
      try {
        const { value: { data = {} } } = await revgeocode_sos_pin(
          this.user.tenant, this.pin_position.lat.toFixed(6), this.pin_position.lng.toFixed(6))
        if (data.address) this.address = data.address
      } catch (e) { /* best-effort: the pin still works without an address */ }
    },
    reset_form() {
      this.submitted = false
      this.submit_error = null
      this.request_id = null
      this.notified = true
      this.vehicle_name = ''
      this.driver_name = ''
      this.driver_phone = ''
      this.driverFromLookup = false
      this.operator_contact_phone = ''
      this.request_notes = ''
      this.address = ''
      this.pin_position = null
      this.default_operator_contact()
    },
    async submit() {
      if (!this.pin_position || this.submitting) return
      this.submitting = true
      this.submit_error = null
      try {
        this.$emit('showAlert', 'Creating SOS', 'info', 10000)
        const res = await send_sos_pin_request(this.user.tenant,
          this.pin_position.lat.toFixed(6), this.pin_position.lng.toFixed(6), {
            vehicle_name: this.vehicle_name,
            driver_name: this.driver_name,
            driver_phone: this.driver_phone,
            operator_contact_name: this.operator_contact_name,
            operator_contact_phone: this.operator_contact_phone,
            operator_contact_email: this.operator_contact_email,
            request_notes: this.request_notes,
            broadcast: false,
          })
        const data = (res && res.value && res.value.data) || {}
        this.request_id = data.request_id || null
        this.notified = data.notified !== false
        this.submitted = true
        if (data.notified === false) {
          // the SOS was recorded but no email/SMS recipient is configured, so nothing was sent
          this.$emit('showAlert', 'SOS recorded, but no SOS email/SMS recipients are configured — no alert was sent. Add recipients in Bus SOS settings.', 'warning', 9000)
        } else {
          this.$emit('showAlert', 'SOS created', 'success', 5000)
        }
      } catch (e) {
        this.submit_error = 'Something went wrong sending the SOS. Try again, or contact support.'
        this.$emit('showAlert', 'Error sending SOS request', 'danger', 6000)
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.sos-send-wrap {
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}
.sos-send-card {
  max-width: 620px;
  width: 100%;
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}
.sos-send-leadrow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}
.sos-send-lead {
  color: var(--fi-text-muted);
}
.sos-send-lead--inline {
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 700;
  color: var(--fi-text);
}
.sos-send-note {
  font-size: 13px;
  color: var(--fi-text-faint);
  margin-bottom: 10px;
}
/* yard-only providers: a quiet reminder that there is no live vehicle position behind the pin */
.sos-send-note--yard-only {
  color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text));
  background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface));
  border: 1px solid color-mix(in srgb, var(--fi-warning) 30%, var(--fi-line));
  border-radius: 8px;
  padding: 7px 10px;
}
.sos-icon-btn {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 1px solid var(--fi-line-strong);
  background: var(--fi-surface);
  border-radius: 8px;
  color: var(--fi-link);
  font-size: 16px;
  cursor: pointer;
}
.sos-icon-btn:hover {
  border-color: var(--fi-link);
}
.sos-icon-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.sos-send-addr {
  display: flex;
  gap: 8px;
  align-items: stretch;
  margin-bottom: 10px;
}
.sos-send-addr .form-control { flex: 1 1 auto; }
.sos-send-addr .sos-icon-btn { flex: 0 0 auto; }
.sos-send-map {
  width: 100%;
  height: 240px;
  border-radius: 10px;
  overflow: hidden;
}
.sos-send-pinline {
  font-size: 14px;
  font-weight: 600;
  color: var(--fi-text);
  margin: 8px 0 4px;
  font-variant-numeric: tabular-nums;
}
.sos-send-pinline--empty {
  font-weight: 400;
  color: var(--fi-text-faint);
}
.sos-pin-clear {
  margin-left: 10px;
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 600;
  color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text));
  background: transparent;
  border: 1px solid color-mix(in srgb, var(--fi-danger) 30%, var(--fi-line));
  border-radius: 5px;
  cursor: pointer;
}
.sos-pin-clear:hover {
  background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface));
}
.sos-send-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 8px 0 4px;
}
.sos-send-field-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.sos-send-field {
  flex: 1 1 150px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
/* operator row: give email more room, keep the phone # compact */
.sos-send-field--wide { flex: 2.2 1 240px; }
.sos-send-field--sm { flex: 0.7 1 120px; }
.sos-send-label {
  display: block;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: var(--fi-text-muted);
  margin: 10px 0 2px;
}
.sos-send-hint {
  text-align: left;
  font-size: 12px;
  color: var(--fi-text-faint);
  margin-top: 3px;
}
.sos-send-state--error {
  color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text));
  background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface));
  border-radius: 8px;
  padding: 10px 12px;
  margin: 10px 0;
}
.sos-send-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}
.sos-send-actions .sos-send-submit {
  flex: 1 1 0;
  margin-top: 0;
}
.sos-send-submit {
  margin-top: 18px;
  font-weight: 700;
}
.sos-send-modes {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
  font-size: 12px;
  color: var(--fi-text-faint);
}
@media (max-width: 520px) {
  .sos-send-actions {
    flex-direction: column;
  }
}
.sos-send-success {
  text-align: center;
}
.sos-send-check {
  width: 64px;
  height: 64px;
  line-height: 64px;
  margin: 8px auto 12px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--fi-success) 12%, var(--fi-surface));
  color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text));
  font-size: 34px;
}
.sos-send-warn-icon {
  width: 64px;
  height: 64px;
  line-height: 64px;
  margin: 8px auto 12px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface));
  color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text));
  font-size: 34px;
}
.sos-send-ref {
  font-variant-numeric: tabular-nums;
  color: var(--fi-text-faint);
  font-size: 14px;
}
.sos-send-successlinks {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 14px;
}
</style>
